import styles from '../SideNavigationStyling.module.scss';
import {SideNavigationMain} from '../side-navigation-main/SideNavigationMain';
import {SideNavigationTertiary} from '../side-navigation-tertiary/SideNavigationTertiary';
import {SideNavigationSecondary} from '../side-navigation-secondary/SideNavigationSecondary';
import {SideNavigationPrimary} from '../side-navigation-primary/SideNavigationPrimary';
import {SideNavigationProfile} from '../side-navigation-profile/SideNavigationProfile';
import {useSideNavigationCollapseContext,} from '../side-navigation-contexts';
import React, {PropsWithChildren} from 'react';
import classNames from 'classnames';
import {TertiaryChildProvider} from '../side-navigation-contexts/SideNavigationTertiaryMenuItemContext';
import {SideNavigationProfileProvider} from '../side-navigation-contexts/SideNavigationProfileProvider';
import {SideNavigationAccordionProvider} from '../side-navigation-contexts';
import {SideNavigationMenuItemType} from '../types/SideNavigationTypes';
import {SideNavigationMenuItemClickProvider} from '../side-navigation-contexts/SideNavigationMenuItemClickContext';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isElementOfSpecificType = <T extends React.ComponentType<any>>(
    element: React.ReactNode,
    component: T
): element is React.ReactElement<React.ComponentProps<T>> => {
    return React.isValidElement(element) && element.type === component;
};

export interface SideNavigationProps {
    onMenuItemClick: (menuItem: SideNavigationMenuItemType, event: React.MouseEvent<HTMLAnchorElement> | undefined) => void;
}

export const SideNavigation = ({onMenuItemClick, children}: PropsWithChildren<SideNavigationProps>) => {

    const {isCollapsed} = useSideNavigationCollapseContext();
    const baseStyling = classNames(
        styles.baseSideNavigationContainer,
        {[styles.collapsed]: isCollapsed},
        {[styles.expanded]: !isCollapsed},
    );

    const innerSideNavigationContainerStyling = classNames(
        styles.innerSideNavigationContainer,
        {[styles.collapsed]: isCollapsed}
    );
    
    // Filter children by type using the type guard
    const mainChild = React.Children.toArray(children).find((child) =>
        isElementOfSpecificType(child, SideNavigationMain)
    );
    const primaryChild = React.Children.toArray(children).find((child) =>
        isElementOfSpecificType(child, SideNavigationPrimary)
    );
    const secondaryChild = React.Children.toArray(children).find((child) =>
        isElementOfSpecificType(child, SideNavigationSecondary)
    );
    const tertiaryChild = React.Children.toArray(children).find((child) =>
        isElementOfSpecificType(child, SideNavigationTertiary)
    );
    const profileChild = React.Children.toArray(children).find((child) =>
        isElementOfSpecificType(child, SideNavigationProfile)
    );

    return (
        <div className={baseStyling} data-profile-supplied={!!profileChild}>
            <div className={innerSideNavigationContainerStyling}>
                {mainChild}
                <SideNavigationMenuItemClickProvider onMenuItemClick={onMenuItemClick}>
                    <SideNavigationAccordionProvider>
                        <div className={styles.primarySecondaryContainer}>
                            {primaryChild}
                            {secondaryChild && <span className={styles.lineSeparator}/>}
                            {secondaryChild}
                        </div>
                    </SideNavigationAccordionProvider>
                    {/* Tertiary children are unique and they need to be aware of that */}
                    <TertiaryChildProvider value={true}>
                        <SideNavigationProfileProvider profileChild={profileChild}>
                            {tertiaryChild}
                        </SideNavigationProfileProvider>
                    </TertiaryChildProvider>
                </SideNavigationMenuItemClickProvider>
            </div>
            {!isCollapsed && profileChild}
        </div>
    );
}

SideNavigation.Main = SideNavigationMain;
SideNavigation.Primary = SideNavigationPrimary;
SideNavigation.Secondary = SideNavigationSecondary;
SideNavigation.Tertiary = SideNavigationTertiary;
SideNavigation.Profile = SideNavigationProfile;